import React from 'react';
import clsx from 'clsx';
import { usePrefersReducedMotion } from '@/hooks/common/use-reduced-motion';
import { approxRateNote } from '@/utils/trades/footer-totals';

interface MetricCellProps {
  label: string;
  value: string;
  alert?: boolean;
  /** What the alert dot MEANS — read by screen readers and shown on hover (LR-542 review #1:
   *  a bare "Alert" tells a non-visual user nothing about the state being flagged). */
  alertLabel?: string;
  isFlashing?: boolean;
  /** Native `title` attribute — used to explain sign conventions on hover. */
  tooltip?: string;
  /** Render a ⚠ marker: the figure used a stale fallback FX rate (mirrors the totals footer). */
  approx?: boolean;
  /** The non-live currencies behind the ⚠ — names them in the marker's tooltip. */
  approxCurrencies?: string[];
}

export const MetricCell: React.FC<MetricCellProps> = ({
  label,
  value,
  alert = false,
  alertLabel = 'Alert',
  isFlashing = false,
  tooltip,
  approx = false,
  approxCurrencies = [],
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const shouldFlash = isFlashing && !prefersReducedMotion;

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 cursor-default" title={tooltip}>
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide whitespace-nowrap">
        {label}
      </span>
      <div className="flex items-center">
        <span
          className={clsx(
            'text-sm font-semibold text-slate-900 font-mono tabular-nums transition-opacity',
            shouldFlash ? 'animate-[fadeInOut_0.6s_ease-in-out]' : '',
          )}
        >
          {value}
        </span>
        {approx && (
          <span
            className="ml-1 text-xs text-amber-600"
            role="img"
            aria-label={approxRateNote(approxCurrencies)}
            title={approxRateNote(approxCurrencies)}
          >
            ⚠
          </span>
        )}
        {alert && (
          <span
            className={clsx(
              'w-2 h-2 rounded-full bg-red-500 inline-block ml-1.5',
              !prefersReducedMotion && 'animate-pulse',
            )}
            role="img"
            aria-label={alertLabel}
            title={alertLabel}
          />
        )}
      </div>
    </div>
  );
};
